import type { NavigationMenuItem } from '@nuxt/ui';

export type SidebarNavItemInput = {
  label: string,
  value: string,
  faIcon: string[],
  to?: string,
  target?: '_blank',
  active?: boolean,
  children?: SidebarNavItemInput[],
  'data-cy'?: string,
};

export type SidebarNavItem = NavigationMenuItem & {
  faIcon?: string[],
  isChild?: boolean,
  'data-cy'?: string,
};

export type NavigationMenuTheme = 'sidebar' | 'profile';

/** Exact path match only — prefix matching incorrectly marks partner roots (e.g. Dashboard) active on nested routes. */
const pathMatches = (routePath: string, to: string): boolean => routePath === to;

/** True when the route is exactly `to` or a nested path under it (e.g. report product tabs). */
export const matchesPathOrDescendant = (routePath: string, to: string): boolean => (
  routePath === to || routePath.startsWith(`${to}/`)
);

const linkUiForTheme = (
  theme: NavigationMenuTheme,
  active: boolean,
  isChild: boolean,
): string => {
  // Top-level: text-base + taller py. Children: next step up from text-sm → text-base.
  let size = 'text-base font-light py-2';
  if (!isChild) {
    size = 'text-base font-normal py-3';
  }
  if (theme === 'profile') {
    return active
      ? `${size} before:bg-(--color-primary-300) text-highlighted`
      : `${size} hover:before:bg-elevated/50`;
  }
  return active
    ? [
      size,
      'before:bg-(--color-sidebar-menu-selected-bg)',
      'text-(--color-sidebar-menu-selected-text)',
      'hover:text-(--color-sidebar-menu-selected-text)',
    ].join(' ')
    : [
      size,
      'text-(--color-sidebar-menu-text)',
      'hover:before:bg-(--color-sidebar-menu-hover)',
      'hover:text-(--color-sidebar-menu-hover-text)',
      'data-[state=open]:before:bg-transparent',
    ].join(' ');
};

export const toNavigationMenuItems = (
  items: SidebarNavItemInput[],
  routePath: string,
  theme: NavigationMenuTheme = 'sidebar',
  isChild = false,
): SidebarNavItem[] => items.map((item) => {
  const children = item.children
    ? toNavigationMenuItems(item.children, routePath, theme, true)
    : undefined;
  const childActive = children?.some((child) => child.active) ?? false;

  // Only the matching leaf/link is active — never paint the parent when a child matches.
  let active = false;
  if (item.active !== undefined) {
    active = item.active;
  } else if (item.to) {
    active = pathMatches(routePath, item.to);
  }

  return {
    label: item.label,
    value: item.value,
    faIcon: item.faIcon,
    isChild,
    to: item.to,
    target: item.target,
    active,
    defaultOpen: childActive,
    type: children?.length ? 'trigger' : 'link',
    children,
    ui: {
      link: linkUiForTheme(theme, active, isChild),
    },
    ...(item['data-cy'] ? { 'data-cy': item['data-cy'] } : {}),
  };
});

/** Partner-colored vertical sidebar menu (matches prior ui/Menu theme + mockups). */
export const sidebarNavigationMenuUi = {
  root: 'flex flex-col gap-0 w-full data-[orientation=vertical]:w-full text-(--color-sidebar-menu-text)',
  list: 'flex flex-col w-full gap-0',
  item: 'mb-1 w-full',
  link: [
    'px-4 rounded-xl gap-2 w-full',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-(--color-sidebar-menu-focus)',
    'before:rounded-xl',
  ].join(' '),
  linkLeadingIcon: 'text-lg',
  linkLabel: '',
  linkTrailingIcon: 'size-4 text-(--color-sidebar-menu-text)',
  childList: 'ms-0 border-s-0 mt-1 flex flex-col gap-0',
  childItem: 'mb-1 ps-2',
} as const;

/** Profile management side nav (non-partner tokens). */
export const profileNavigationMenuUi = {
  root: 'flex flex-col gap-0 w-full data-[orientation=vertical]:w-full',
  list: 'flex flex-col w-full gap-0',
  item: 'mb-2 w-full',
  link: 'px-4 py-2 rounded-xl gap-2 w-full before:rounded-xl text-base',
  linkLabel: '',
  linkTrailingIcon: 'size-5',
} as const;
